import { Status } from '@philo/web-shared';
import { useCallback } from 'react';
import { SECURE_BASE_URL } from '../../config';
import {
  setDeviceAuthCodeStatus,
  setDeviceAuthCodeValidationError,
  setIsValidatingDeviceAuthCode,
  updateDeviceAuthState,
} from '../../redux/actions/device-auth';
import {
  getDeviceAuthRedirectURL,
  getIsValidatingDeviceAuthCode,
} from '../../redux/selectors/device-auth';
import { RESPONSE_STATUS_SUCCESS } from '../../utils/auth-errors';
import { CATCH_ERROR } from '../../utils/defaults';
import { getHumanReadableErrorMessage } from '../../utils/device-auth';
import {
  ACCOUNT_NESTED,
  PLAYER,
  createActivateURL,
  createAppPathAndPersistQuery,
} from '../../utils/router';
import { JSONResponse } from '../../web-shared/types/billing';
import { checkResponse, extractErrorMessage } from '../../web-shared/utils/billing';
import useInAppNavigate from '../router/use-in-app-navigate';
import useRedirectToPlayer from '../router/use-redirect-to-player';
import useAppStore from '../use-app-store';

export const VALIDATE_AUTH_CODE_URL = `${SECURE_BASE_URL}/auth/update/activate`;
export const DEFAULT_DEVICE_AUTH_ERROR_MESSAGE =
  'Unable to activate your device. Please try again later';

/** KU
 *
 * Returns a handler, {@link handleActivateDevice}, which attempts to activate a
 * Smart TV using the provided `code`. If successful, this handler will
 * redirect the user to `/player`. Otherwise, the user is redirected to
 * `/go/activate` and an error message is displayed.
 * */
export default function useActivateDevice() {
  const { getState, dispatch } = useAppStore();

  const navigate = useInAppNavigate();
  const { handleRedirectToPlayer } = useRedirectToPlayer();

  const handleActivateDevice = useCallback((code: string) => {
    const state = getState();

    const isValidating = getIsValidatingDeviceAuthCode(state);
    const activateURL = createActivateURL(code);

    if (isValidating || !code) {
      return;
    }

    const handleError = (error = DEFAULT_DEVICE_AUTH_ERROR_MESSAGE) => {
      const helpfulError = getHumanReadableErrorMessage(error);
      dispatch(
        updateDeviceAuthState({
          status: Status.INVALID,
          validationError: helpfulError,
          isValidatingCode: false,
        }),
      );
      // KU – redirect to /go/activate page
      navigate(activateURL, { replace: true });
    };

    const handleSuccess = (json: Record<string, string> & JSONResponse) => {
      if (!json.success && json.status !== RESPONSE_STATUS_SUCCESS) {
        const { errorMessage } = extractErrorMessage(
          json,
          json.description || DEFAULT_DEVICE_AUTH_ERROR_MESSAGE,
        );
        handleError(errorMessage);
        return;
      }
      dispatch(setDeviceAuthCodeStatus(Status.VALID));
      const initialURL = getDeviceAuthRedirectURL(state) || ACCOUNT_NESTED;
      const redirectURL = createAppPathAndPersistQuery(
        initialURL === PLAYER ? ACCOUNT_NESTED : initialURL,
        {
          deviceActivated: true,
        },
      );
      handleRedirectToPlayer(redirectURL);
    };

    dispatch(setIsValidatingDeviceAuthCode(true));
    dispatch(setDeviceAuthCodeValidationError(''));

    fetch(VALIDATE_AUTH_CODE_URL, {
      method: 'POST',
      body: JSON.stringify({
        token: code,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    })
      .then(checkResponse)
      .then(handleSuccess)
      .catch((error: Error) => {
        CATCH_ERROR(error);
        handleError();
      });
  }, []);

  return { handleActivateDevice };
}
